import {
  browseModeStore,
  configStore,
  documentStore,
  uiStore
} from "@/store/store";
import { getFrontendUrl } from "@/lib/document";
import { closeAllUiPanels, openUiPanel } from "@/lib/panels/panels";
import {
  handleGlobalSelectorsChanged,
  handleGlobalSettingsChanged
} from "@/lib/canvas/reacting-to-global-store-changes";
import { requestExitFromBuilder } from "@/lib/builder";
import {
  setWindowBreakdanceCanvasDocument,
  setWindowBreakdanceObjectAssetsFunctionsWithGivenDocumentBounded
} from "@/lib/canvas/helpers";
import { injectBreakdanceBrowseModeParameters } from "@/lib/browse-mode/param-injection";
import { initializeOrReinitializeCombokeys } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import { UIPanel } from "@/types/ui";
import { addOrUpdateInlineCSSAtEndOfHead } from "@/lib/canvas/assets/dom-utils";

// @ts-ignore
import iframeCss from "./../../components/BrowseMode/styles.iframecss";
import { breakdanceUndo } from "@/lib/undo/undo";
import { clearMemoizedTwigFunctionsCache } from "@/lib/api/api";

export const BREAKDANCE_BROWSE_MODE_QUERY_PARAM_NAME = "breakdance_browser";

export type BrowseModeSupportedUIPanels = Extract<
  UIPanel,
  "globalSettings" | "selectors" | "history"
>;

export function updateBrowseModePageTitle() {
  const titleParts = ["Breakdance", "Global Styles"];

  const iframeDocumentTitleToDisplay =
    browseModeStore.iframeDocumentTitleToDisplay;

  if (iframeDocumentTitleToDisplay) {
    titleParts.push(iframeDocumentTitleToDisplay);
  }

  window.document.title = titleParts.join(" | ");
}

export function getUrlToUseForBrowseMode() {
  return (
    getFrontendUrl() ??
    configStore.neededData?.browseModeAllowedUrls?.[0] ??
    undefined
  );
}

export function isBrowseModeAvailable(): boolean {
  return getUrlToUseForBrowseMode() !== undefined;
}

export function toggleBrowseMode(panelToOpen: BrowseModeSupportedUIPanels) {
  if (uiStore.mode === "browse") {
    disableBrowseMode();
  } else {
    enableBrowseMode(panelToOpen);
  }
}

export function enableBrowseMode(
  panelToOpen: BrowseModeSupportedUIPanels = "globalSettings",
  urlToNavigateTo?: string
): boolean {
  if (urlToNavigateTo === undefined) {
    urlToNavigateTo = getUrlToUseForBrowseMode();
  }

  if (uiStore.mode === "builder") {
    if (urlToNavigateTo === undefined) {
      return false;
    }

    // Make sure builder closing confirmation is handled if any
    if (!requestExitFromBuilder()) {
      return false;
    }

    // Make sure panel closing confirmations are handled if any
    if (!closeAllUiPanels()) {
      return false;
    }

    uiStore.setMode("browse");
  }

  breakdanceUndo.initialize();

  openUiPanel(panelToOpen);
  uiStore.setInterfaceState("normal");

  if (urlToNavigateTo) {
    navigateBrowseModeIframeToUrl(urlToNavigateTo);
  }

  return true;
}

export function navigateBrowseModeIframeToUrl(url: string) {
  browseModeStore.setIframeUrl(upsertBreakdanceBrowserParamToUrl(url));
}

export function disableBrowseMode() {
  // Make sure panel closing confirmations are handled if any
  if (closeAllUiPanels()) {
    if (
      window.Breakdance.initialMode === "browse" &&
      browseModeStore.backToAdminUrl
    ) {
      window.location.href = browseModeStore.backToAdminUrl;
    } else {
      uiStore.setMode("builder");
      breakdanceUndo.ignore(() => documentStore.setNoDocument());
      uiStore.setInterfaceState("loading");
    }
  }
}

export function handleBrowseModeIframeLoaded(
  iframe: HTMLIFrameElement
): Document {
  if (!iframe.contentWindow || !iframe.contentDocument) {
    throw "Unable to proceed without iframe document";
  }
  iframe.contentWindow.onbeforeunload = handleBrowseModeIframeUnloaded;

  injectBreakdanceBrowseModeParameters(
    iframe.contentDocument,
    iframe.contentWindow
  );

  clearMemoizedTwigFunctionsCache();

  browseModeStore.setIframeState("normal");
  browseModeStore.setIframeDocumentTitle(iframe.contentDocument.title);
  browseModeStore.setIframeWindowUrl(iframe.contentWindow.location.href);

  linkWindowBreakdanceCanvasObjectToIframeDocument(iframe.contentDocument);

  handleGlobalSettingsChanged(iframe.contentDocument);
  handleGlobalSelectorsChanged(iframe.contentDocument);

  initializeOrReinitializeCombokeys(document, iframe.contentDocument);

  addOrUpdateInlineCSSAtEndOfHead(
    iframe.contentDocument,
    "breakdance-browse-mode-css",
    iframeCss
  );

  return iframe.contentDocument;
}

export function linkWindowBreakdanceCanvasObjectToIframeDocument(
  iframeDocument: Document
) {
  setWindowBreakdanceCanvasDocument(iframeDocument);
  setWindowBreakdanceObjectAssetsFunctionsWithGivenDocumentBounded(
    () => iframeDocument
  );
}

export function handleBrowseModeIframeUnloaded() {
  // browseModeStore.setIframeState("loading");
}

export function upsertBreakdanceBrowserParamToUrl(
  url: string,
  base?: string | URL
): string {
  try {
    // May throw when constructing a URL object
    const urlObject = new URL(url, base);

    if (!urlObject.searchParams.has(BREAKDANCE_BROWSE_MODE_QUERY_PARAM_NAME)) {
      urlObject.searchParams.append(
        BREAKDANCE_BROWSE_MODE_QUERY_PARAM_NAME,
        "true"
      );
    }

    return urlObject.toString();
  } catch (err) {
    console.warn(
      "Browse mode: upsertBreakdanceBrowserParamToUrl: invalid URL passed",
      err
    );
    return url;
  }
}

export function removeBreakdanceBrowserParamFromUrl(
  url: string,
  base?: string | URL
): string {
  const urlObject = new URL(url, base);

  if (urlObject.searchParams.has(BREAKDANCE_BROWSE_MODE_QUERY_PARAM_NAME)) {
    urlObject.searchParams.delete(BREAKDANCE_BROWSE_MODE_QUERY_PARAM_NAME);
  }

  return urlObject.toString();
}
